export const REFERENCE_OFFER_TABS = Object.freeze({
  ONGOING: 'ONGOING',
  CANCELED: 'CANCELED',
  FINISHED: 'FINISHED',
});

export const REFERENCE_DEFAULT_OFFER_LIST_SIZE = 10;

export const REFERENCE_DEFAULT_OFFER_PAGE_ORIGIN = 'menu';

export const REFERENCE_OFFER_TYPES = Object.freeze({
  FINANCED: 'FINANCED',
  CASH: 'CASH',
  FINANCED_USING_FGTS: 'FINANCED_USING_FGTS',
  CASH_USING_FGTS: 'CASH_USING_FGTS',
});

export const REFERENCE_ACTIVITY_MONTHS = Object.freeze({
  ONE: '1',
  TWO: '2',
  THREE: '3',
  FOUR: '4',
  SIX: '6',
  TWELVE: '12',
});

export const REFERENCE_ACTIVITY_PRESET_MONTHS = Object.freeze(
  Object.values(REFERENCE_ACTIVITY_MONTHS).map(Number)
);

export const REFERENCE_SORTING_OPTIONS = Object.freeze({
  ARRIVAL_DATE: 'created_at,desc',
  RECENT_ACTIVITY: 'updated_at,desc',
  HIGHER_VALUE: 'o.offer_price,desc',
  LOWER_VALUE: 'o.offer_price,asc',
});

export const REFERENCE_OFFER_STATUS = Object.freeze({
  NEGOTIATION_NEW: 'NEGOTIATION_NEW',
  NEGOTIATION_ONGOING: 'NEGOTIATION_ONGOING',
  NEGOTIATION_OFFER_ACCEPTED: 'NEGOTIATION_OFFER_ACCEPTED',
  CCV_SENT_TO_BE_SIGNED: 'CCV_SENT_TO_BE_SIGNED',
  CCV_SIGNED: 'CCV_SIGNED',
  CREDIT_ANALYSIS: 'CREDIT_ANALYSIS',
  OFFER_FINISHED: 'OFFER_FINISHED',
});

export const REFERENCE_CANCELED_REASONS = Object.freeze({
  COUNTER_OFFER_REJECTED: 'COUNTER_OFFER_REJECTED',
  BUYER_GAVE_UP_TO_BUY_THIS_PROPERTY: 'BUYER_GAVE_UP_TO_BUY_THIS_PROPERTY',
  GAVE_UP_OFFER: 'GAVE_UP_OFFER',
  NO_RESPONSE: 'NO_RESPONSE',
  DEAL_WITH_ANOTHER_REAL_STATE: 'DEAL_WITH_ANOTHER_REAL_STATE',
  ALREADY_DEALT_WITH_ANOTHER_PERSON: 'ALREADY_DEALT_WITH_ANOTHER_PERSON',
  PROBLEMATIC_HOUSE_DOCUMENTATION: 'PROBLEMATIC_HOUSE_DOCUMENTATION',
  SUSPENDED_SELL: 'SUSPENDED_SELL',
  CREDIT_NOT_APPROVED: 'CREDIT_NOT_APPROVED',
  SELLER_ONLY_ACCEPTS_LISTING_PRICE: 'SELLER_ONLY_ACCEPTS_LISTING_PRICE',
  DEFAULT_CANCELED_REASON: 'DEFAULT_CANCELED_REASON',
});

export const REFERENCE_OFFER_MACRO_STATUS = Object.freeze({
  NEW: 'NEW',
  NEGOTIATION: 'NEGOTIATION',
  OFFER_ACCEPTED: 'OFFER_ACCEPTED',
  CCV: 'CCV',
  FINISHED: 'FINISHED',
  CANCELED: 'CANCELED',
});

export const REFERENCE_OFFER_STEPS_BY_STATUS = Object.freeze({
  [REFERENCE_OFFER_MACRO_STATUS.NEW]: Object.freeze([
    REFERENCE_OFFER_STATUS.NEGOTIATION_NEW,
    'PROPOSAL_ON_VALIDATION',
  ]),
  [REFERENCE_OFFER_MACRO_STATUS.NEGOTIATION]: Object.freeze([
    REFERENCE_OFFER_STATUS.NEGOTIATION_ONGOING,
    'PROPOSAL_ONGOING',
  ]),
  [REFERENCE_OFFER_MACRO_STATUS.OFFER_ACCEPTED]: Object.freeze([
    REFERENCE_OFFER_STATUS.NEGOTIATION_OFFER_ACCEPTED,
    'PROPOSAL_ACCEPTED',
  ]),
  [REFERENCE_OFFER_MACRO_STATUS.CCV]: Object.freeze([
    REFERENCE_OFFER_STATUS.CREDIT_ANALYSIS,
    REFERENCE_OFFER_STATUS.CCV_SENT_TO_BE_SIGNED,
    REFERENCE_OFFER_STATUS.CCV_SIGNED,
  ]),
  [REFERENCE_OFFER_MACRO_STATUS.FINISHED]: Object.freeze([
    REFERENCE_OFFER_STATUS.OFFER_FINISHED,
    'PURCHASE_SALE_COMPLETED',
    'POST_CCV_HUB_CT',
    'POST_CCV',
  ]),
  [REFERENCE_OFFER_MACRO_STATUS.CANCELED]: Object.freeze([
    'CANCELED_CCV',
    'CANCELED_ON_VALIDATION',
    'CANCELED_ON_NEGOTIATION',
    'CANCELED_POST_ACCEPTED',
  ]),
});

export const isReferenceOfferStepCanceled = stepKey =>
  Object.values(REFERENCE_CANCELED_REASONS).includes(stepKey);

export const referenceOfferStatusByStep = (step, ccv = null) => {
  if (isReferenceOfferStepCanceled(step)) {
    return REFERENCE_OFFER_MACRO_STATUS.CANCELED;
  }

  const status = Object.keys(REFERENCE_OFFER_STEPS_BY_STATUS).find(key =>
    REFERENCE_OFFER_STEPS_BY_STATUS[key].includes(step)
  );

  if (status === REFERENCE_OFFER_MACRO_STATUS.OFFER_ACCEPTED && ccv) {
    return REFERENCE_OFFER_MACRO_STATUS.CCV;
  }

  return status || null;
};

export const referenceOfferTimelineStateForStep = step => {
  if (isReferenceOfferStepCanceled(step?.key)) return 'cancelado';
  if (step?.startDate || step?.start_date || step?.completedAt) {
    return 'concluido';
  }
  return 'pendente';
};

export const LOCAL_OFFER_BUCKET_BY_REFERENCE_TAB = Object.freeze({
  [REFERENCE_OFFER_TABS.ONGOING]: 'in_negotiation',
  [REFERENCE_OFFER_TABS.CANCELED]: 'canceled',
  [REFERENCE_OFFER_TABS.FINISHED]: 'finished',
});

export const REFERENCE_TAB_BY_LOCAL_OFFER_BUCKET = Object.freeze({
  in_negotiation: REFERENCE_OFFER_TABS.ONGOING,
  canceled: REFERENCE_OFFER_TABS.CANCELED,
  finished: REFERENCE_OFFER_TABS.FINISHED,
});

export const REFERENCE_SORT_BY_LOCAL_SORT = Object.freeze({
  recent_activity: REFERENCE_SORTING_OPTIONS.RECENT_ACTIVITY,
  arrival_date: REFERENCE_SORTING_OPTIONS.ARRIVAL_DATE,
  higher_value: REFERENCE_SORTING_OPTIONS.HIGHER_VALUE,
  lower_value: REFERENCE_SORTING_OPTIONS.LOWER_VALUE,
});

export const LOCAL_SORT_BY_REFERENCE_SORT = Object.freeze({
  [REFERENCE_SORTING_OPTIONS.RECENT_ACTIVITY]: 'recent_activity',
  [REFERENCE_SORTING_OPTIONS.ARRIVAL_DATE]: 'arrival_date',
  [REFERENCE_SORTING_OPTIONS.HIGHER_VALUE]: 'higher_value',
  [REFERENCE_SORTING_OPTIONS.LOWER_VALUE]: 'lower_value',
});

export const LOCAL_PAYMENT_KIND_BY_REFERENCE_OFFER_TYPE = Object.freeze({
  [REFERENCE_OFFER_TYPES.CASH]: 'a_vista',
  [REFERENCE_OFFER_TYPES.FINANCED]: 'financiamento',
  [REFERENCE_OFFER_TYPES.CASH_USING_FGTS]: 'a_vista_fgts',
  [REFERENCE_OFFER_TYPES.FINANCED_USING_FGTS]: 'financiamento_fgts',
});

export const REFERENCE_OFFER_TYPE_BY_LOCAL_PAYMENT_KIND = Object.freeze({
  a_vista: REFERENCE_OFFER_TYPES.CASH,
  financiamento: REFERENCE_OFFER_TYPES.FINANCED,
  a_vista_fgts: REFERENCE_OFFER_TYPES.CASH_USING_FGTS,
  financiamento_fgts: REFERENCE_OFFER_TYPES.FINANCED_USING_FGTS,
});

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const compactObject = object =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null && value !== undefined;
    })
  );

const isIsoDate = value => {
  if (!ISO_DATE_PATTERN.test(String(value || ''))) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime());
};

const toIsoDate = value => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

export const referenceTabForBucket = bucket =>
  REFERENCE_TAB_BY_LOCAL_OFFER_BUCKET[bucket] || REFERENCE_OFFER_TABS.ONGOING;

export const bucketForReferenceTab = tab =>
  LOCAL_OFFER_BUCKET_BY_REFERENCE_TAB[tab] || 'in_negotiation';

export const referenceSortForLocalSort = sort =>
  REFERENCE_SORT_BY_LOCAL_SORT[sort] ||
  REFERENCE_SORTING_OPTIONS.RECENT_ACTIVITY;

export const localSortForReferenceSort = sort =>
  LOCAL_SORT_BY_REFERENCE_SORT[sort] || 'recent_activity';

export const referenceOfferTypeForLocalPaymentKind = paymentKind =>
  REFERENCE_OFFER_TYPE_BY_LOCAL_PAYMENT_KIND[paymentKind] || null;

export const localPaymentKindForReferenceOfferType = offerType =>
  LOCAL_PAYMENT_KIND_BY_REFERENCE_OFFER_TYPE[offerType] || 'all';

export const normalizeReferenceActivityFilter = (filter = {}) => {
  const months = String(filter.months || '');
  const allowedMonths = Object.values(REFERENCE_ACTIVITY_MONTHS);
  if (allowedMonths.includes(months)) {
    return { months, startDate: '', endDate: '' };
  }

  const startDate = isIsoDate(filter.startDate) ? filter.startDate : '';
  const endDate = isIsoDate(filter.endDate) ? filter.endDate : '';
  if (!startDate || !endDate || endDate < startDate) {
    return { months: '', startDate: '', endDate: '' };
  }

  return { months: '', startDate, endDate };
};

export const referenceActivityRangeForFilter = (
  filter = {},
  now = new Date()
) => {
  const normalized = normalizeReferenceActivityFilter(filter);
  if (normalized.months) {
    const start = new Date(now);
    start.setMonth(start.getMonth() - Number(normalized.months));
    return {
      startDate: toIsoDate(start),
      endDate: toIsoDate(now),
    };
  }

  if (normalized.startDate && normalized.endDate) {
    return {
      startDate: normalized.startDate,
      endDate: normalized.endDate,
    };
  }

  return { startDate: null, endDate: null };
};

export const submittedRangeForReferenceActivityFilter = (
  filter = {},
  now = new Date()
) => {
  const range = referenceActivityRangeForFilter(filter, now);
  return compactObject({
    submitted_from: range.startDate,
    submitted_to: range.endDate,
  });
};

export const buildReferenceOfferListParams = ({
  pageSize,
  perPage,
  currentOfferSize,
  sortBy = 'recent_activity',
  statusBucket = 'in_negotiation',
  clientId = null,
  paymentKind = 'all',
  activityFilter = {},
  now = new Date(),
} = {}) => {
  const activityRange = referenceActivityRangeForFilter(activityFilter, now);
  return compactObject({
    sort: referenceSortForLocalSort(sortBy),
    size:
      currentOfferSize ||
      pageSize ||
      perPage ||
      REFERENCE_DEFAULT_OFFER_LIST_SIZE,
    clientId,
    status: referenceTabForBucket(statusBucket),
    paymentMethod:
      paymentKind === 'all'
        ? null
        : referenceOfferTypeForLocalPaymentKind(paymentKind),
    startDate: activityRange.startDate,
    endDate: activityRange.endDate,
  });
};

export const buildNativeOfferListParams = ({
  page = 1,
  perPage,
  currentOfferSize,
  sortBy = 'recent_activity',
  statusBucket = 'in_negotiation',
  negotiationStatus = 'all',
  kind = 'all',
  businessContext = 'all',
  paymentKind = 'all',
  activityFilter = {},
  rentProgressPhase = '',
  contractPeriod = '',
  contractStatus = '',
  documentationPeriod = '',
  searchTerm = '',
  contactId = '',
  imovelId = '',
  now = new Date(),
} = {}) =>
  compactObject({
    page,
    per_page: currentOfferSize || perPage || REFERENCE_DEFAULT_OFFER_LIST_SIZE,
    sort: referenceSortForLocalSort(sortBy),
    status_bucket: statusBucket,
    status:
      statusBucket === 'in_negotiation' &&
      negotiationStatus !== 'all' &&
      negotiationStatus !== 'post_ccv'
        ? negotiationStatus
        : '',
    kind: kind === 'all' ? '' : kind,
    business_context: businessContext === 'all' ? '' : businessContext,
    payment_kind: paymentKind === 'all' ? '' : paymentKind,
    rent_progress_phase: rentProgressPhase,
    contract_period: contractPeriod,
    contract_status: contractStatus,
    documentation_period: documentationPeriod,
    contact_id: contactId,
    imovel_id: imovelId,
    ...submittedRangeForReferenceActivityFilter(activityFilter, now),
    q: String(searchTerm || '').trim(),
  });

export const offerReferenceIdentifiers = offer =>
  [
    offer?.salesFlowId,
    offer?.sales_flow_id,
    offer?.id,
    offer?.property_offer_id,
    offer?.bookingReference,
    offer?.booking_reference,
    offer?.token,
  ]
    .filter(value => value !== null && value !== undefined && value !== '')
    .map(value => String(value));

export const offerMatchesReferenceIdentifier = (offer, identifier) => {
  if (!identifier) return false;
  return offerReferenceIdentifiers(offer).includes(String(identifier));
};

export const mergeReferenceDeepLinkedOffer = ({
  offers = [],
  offerIdFromParams,
  offerByIdData,
  loadedAllOffers = true,
} = {}) => {
  if (!offerIdFromParams || !loadedAllOffers || !offerByIdData) {
    return offers;
  }

  const exists = offers.some(offer =>
    offerMatchesReferenceIdentifier(offer, offerIdFromParams)
  );
  if (exists) return offers;

  return [
    ...offers,
    {
      ...offerByIdData,
      salesFlowId: offerByIdData.salesFlowId || offerIdFromParams,
      sales_flow_id: offerByIdData.sales_flow_id || offerIdFromParams,
    },
  ];
};
